<template>
  <div class="min-h-screen" :class="currentThemeClass">
    <!-- Theme Toggle -->
    <div class="fixed top-4 right-4 z-10">
      <button 
        @click="toggleTheme"
        class="theme-toggle-btn p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        :title="isLightTheme ? 'Switch to Dark Theme' : 'Switch to Light Theme'"
      >
        <Sun v-if="!isLightTheme" class="w-6 h-6 text-yellow-400" />
        <Moon v-else class="w-6 h-6 text-gray-600" />
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" :class="isLightTheme ? 'border-blue-500' : 'border-primary-400'"></div>
        <p class="text-lg" :class="isLightTheme ? 'text-gray-700' : 'text-white'">Loading menu...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center justify-center min-h-screen">
      <div class="text-center" :class="isLightTheme ? 'text-gray-700' : 'text-white'">
        <AlertCircle class="w-16 h-16 mx-auto mb-4 text-red-400" />
        <h2 class="text-2xl font-bold mb-2">Menu Not Available</h2>
        <p :class="isLightTheme ? 'text-gray-600' : 'text-gray-300'">{{ error }}</p>
      </div>
    </div>

    <!-- Menu Content -->
    <div class="px-6 py-8">
      <div class="max-w-6xl mx-auto">
        <!-- Preview Mode Banner -->
        <div v-if="isPreviewMode" class="preview-banner p-4 mb-6">
          <p class="font-medium text-center" :class="isLightTheme ? 'text-amber-700' : 'text-yellow-200'">
            📋 Menu Preview Mode - This is how your menu will appear to customers
          </p>
        </div>
        
        <!-- Restaurant Header -->
        <div class="menu-card p-8 mb-8">
          <div class="text-center">
            <h1 class="text-4xl lg:text-5xl font-black menu-title mb-4">{{ menuData?.location?.name || 'Loading...' }}</h1>
            <div class="menu-info space-y-2 max-w-2xl mx-auto">
              <p v-if="menuData?.location?.address && menuData.location.address !== 'Preview Mode'" class="flex items-center justify-center">
                <MapPin class="w-5 h-5 mr-2 menu-icon" />
                {{ menuData.location.address }}
              </p>
              <p v-if="menuData?.location?.phone" class="flex items-center justify-center">
                <Phone class="w-5 h-5 mr-2 menu-icon" />
                {{ menuData.location.phone }}
              </p>
              <p v-if="menuData?.location?.openingHours" class="flex items-center justify-center">
                <Clock class="w-5 h-5 mr-2 menu-icon" />
                {{ menuData.location.openingHours }}
              </p>
            </div>
            <p v-if="menuData?.menu?.description" class="menu-info mt-4 text-lg">{{ menuData.menu.description }}</p>
          </div>
        </div>

        <!-- Menu Categories -->
        <div v-if="sortedCategories && sortedCategories.length > 0" class="space-y-8">
          <div v-for="category in sortedCategories" :key="category.category" class="menu-card p-8">
            <h2 class="text-3xl font-bold menu-title mb-8 text-center">
              {{ category.category || 'Other Items' }}
            </h2>
            
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="item in category.items" :key="item.id" class="menu-item-card p-6">
                <!-- Item Image -->
                <div v-if="item.imageUrl" class="w-full h-48 rounded-lg overflow-hidden mb-4">
                  <img 
                    :src="item.imageUrl" 
                    :alt="item.name"
                    class="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    @error="hideImage"
                  />
                </div>
                
                <!-- Item Details -->
                <div class="space-y-3">
                  <div class="flex justify-between items-start">
                    <h3 class="text-xl font-bold menu-item-title">{{ item.name }}</h3>
                    <span class="text-xl font-black menu-price">{{ formatPrice(item.price) }}</span>
                  </div>
                  
                  <p v-if="item.description" class="menu-item-description leading-relaxed">{{ item.description }}</p>
                  
                  <!-- Additional Info -->
                  <div class="space-y-2">
                    <!-- Preparation Time -->
                    <div v-if="item.preparationTime > 0" class="flex items-center text-sm menu-extra-info">
                      <Clock class="w-4 h-4 mr-2" />
                      {{ item.preparationTime }} mins prep time
                    </div>
                    
                    <!-- Allergens -->
                    <div v-if="item.allergens" class="flex items-center text-sm menu-allergen-info">
                      <AlertTriangle class="w-4 h-4 mr-2" />
                      Contains: {{ formatAllergens(item.allergens) }}
                    </div>
                    
                    <!-- Nutritional Info -->
                    <div v-if="item.nutritionalInfo" class="menu-nutritional-info text-xs">
                      {{ formatNutritionalInfo(item.nutritionalInfo) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Menu State -->
        <div v-else class="text-center py-16">
          <div class="max-w-md mx-auto">
            <div class="w-24 h-24 mx-auto mb-6 rounded-full bg-dark-700 flex items-center justify-center">
              <UtensilsIcon class="w-12 h-12 text-gray-400" />
            </div>
            <h3 class="text-2xl font-bold mb-4" :class="isLightTheme ? 'text-gray-800' : 'text-white'">
              No Menu Items Yet
            </h3>
            <p class="text-lg mb-6" :class="isLightTheme ? 'text-gray-600' : 'text-gray-300'">
              This menu doesn't have any categories or items yet.
            </p>
            <div v-if="isPreviewMode" class="bg-amber-100 border border-amber-300 rounded-lg p-4 text-amber-800">
              <p class="font-medium">📝 Menu Preview Mode</p>
              <p class="text-sm mt-1">Add categories and menu items in the admin dashboard to see them here.</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="menu-footer text-center mt-16 pt-8">
          <div class="inline-flex items-center space-x-2">
            <span>Powered by</span>
            <span class="menu-title font-bold">Digital Menu AI</span>
          </div>
          <p v-if="menuData?.location?.email" class="menu-info text-sm mt-2">
            Questions? Contact us at {{ menuData.location.email }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MapPin, Phone, Clock, AlertCircle, AlertTriangle, Sun, Moon, UtensilsIcon } from 'lucide-vue-next'
import { apiService } from '@/services/api'
import { useToast } from '@/composables/useToast'
import { formatPriceForPublic } from '@/utils/currency'

export default {
  name: 'PublicMenuView',
  components: {
    MapPin,
    Phone,
    Clock,
    AlertCircle,
    AlertTriangle,
    Sun,
    Moon,
    UtensilsIcon
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      loading: true,
      error: null,
      menuData: null,
      locationId: null,
      menuId: null,
      isPreviewMode: false,
      isLightTheme: false,
      userPreference: null
    }
  },
  computed: {
    currentThemeClass() {
      return this.isLightTheme ? 'light-theme' : 'dark-theme'
    },
    sortedCategories() {
      if (!this.menuData?.categories) return []
      
      // Sort categories by order if they have it, fallback to logical default order
      return [...this.menuData.categories].sort((a, b) => {
        // If both have order field, sort by order
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order
        }
        
        // If only one has order, prioritize it
        if (a.order !== undefined) return -1
        if (b.order !== undefined) return 1
        
        // If neither has order, use logical category ordering
        const categoryOrder = {
          'appetizers': 1,
          'starters': 1,
          'salads': 2,
          'soups': 3,
          'main courses': 4,
          'mains': 4,
          'entrees': 4,
          'sides': 5,
          'desserts': 6,
          'beverages': 7,
          'drinks': 7,
          'cocktails': 8,
          'wine': 9,
          'beer': 10
        }
        
        const aOrder = categoryOrder[a.category?.toLowerCase()] || 99
        const bOrder = categoryOrder[b.category?.toLowerCase()] || 99
        
        if (aOrder !== bOrder) {
          return aOrder - bOrder
        }
        
        // Fallback to alphabetical
        return (a.category || '').localeCompare(b.category || '')
      })
    }
  },
  async mounted() {
    // Initialize theme based on time of day or user preference
    this.initializeTheme()
    
    this.locationId = this.$route.params.locationId
    this.menuId = this.$route.params.menuId
    this.isPreviewMode = !!this.menuId
    
    if (this.locationId) {
      await this.loadPublicMenu()
    } else if (this.menuId) {
      await this.loadMenuPreview()
    } else {
      this.error = 'Invalid parameters'
      this.loading = false
    }
  },
  methods: {
    initializeTheme() {
      // Check for user preference first
      this.userPreference = localStorage.getItem('menuThemePreference')
      
      if (this.userPreference) {
        this.isLightTheme = this.userPreference === 'light'
      } else {
        // Use time-based theme selection
        const currentHour = new Date().getHours()
        // Light theme: 6 AM (6) to 6 PM (18)
        // Dark theme: 6 PM (18) to 6 AM (6)
        this.isLightTheme = currentHour >= 6 && currentHour < 18
      }
    },
    
    toggleTheme() {
      this.isLightTheme = !this.isLightTheme
      const newTheme = this.isLightTheme ? 'light' : 'dark'
      
      // Save user preference
      localStorage.setItem('menuThemePreference', newTheme)
      this.userPreference = newTheme
    },
    async loadPublicMenu() {
      try {
        this.loading = true
        this.error = null
        
        // Handle demo case with sample data
        if (this.locationId === 'demo') {
          const demoResponse = this.getDemoMenuData()
          this.menuData = demoResponse.data
          this.loading = false
          return
        }
        
        const response = await apiService.getPublicMenu(this.locationId)
        if (response.success) {
          this.menuData = response.data
        } else {
          this.error = response.message || 'Menu not available'
        }
      } catch (error) {
        console.error('Error loading public menu:', error)
        this.error = error.message || 'Failed to load menu'
      } finally {
        this.loading = false
      }
    },

    async loadMenuPreview() {
      try {
        this.loading = true
        this.error = null
        
        const response = await apiService.getMenuPreview(this.menuId)
        if (response.success) {
          this.menuData = response.data
        } else {
          this.error = response.message || 'Menu not available'
        }
      } catch (error) {
        console.error('Error loading menu preview:', error)
        this.error = error.message || 'Failed to load menu'
      } finally {
        this.loading = false
      }
    },
    
    hideImage(event) {
      event.target.style.display = 'none'
    },
    
    formatAllergens(allergensJson) {
      try {
        const allergens = JSON.parse(allergensJson)
        return Array.isArray(allergens) ? allergens.join(', ') : allergensJson
      } catch {
        return allergensJson
      }
    },
    
    formatNutritionalInfo(nutritionalJson) {
      try {
        const info = JSON.parse(nutritionalJson)
        if (typeof info === 'object') {
          return Object.entries(info)
            .map(([key, value]) => `${key}: ${value}`)
            .join(' | ')
        }
        return nutritionalJson
      } catch {
        return nutritionalJson
      }
    },
    
    formatPrice(price, currency = null) {
      // Use passed currency or fallback to menu currency, default to USD if not available
      const currencyCode = currency || this.menuData?.menu?.currency || 'USD'
      return formatPriceForPublic(price, currencyCode)
    },

    getDemoMenuData() {
      return {
        success: true,
        data: {
          location: {
            id: 'demo',
            name: 'Bella Vista Restaurant',
            address: '123 Culinary Street, Food District, City',
            phone: '+1 (555) 123-4567',
            email: 'hello@bellavista.com',
            openingHours: 'Monday-Sunday: 11:00 AM - 11:00 PM'
          },
          menu: {
            id: 'demo-menu',
            name: 'Main Menu',
            description: 'Our signature collection of carefully crafted dishes',
            currency: 'USD'
          },
          categories: [
            {
              category: 'Appetizers',
              order: 1,
              items: [
                {
                  id: 'app1',
                  name: 'Truffle Arancini',
                  description: 'Crispy risotto balls stuffed with truffle cheese, served with garlic aioli',
                  price: 16.99,
                  imageUrl: null,
                  allergens: 'Dairy, Gluten',
                  nutritionalInfo: '{"Calories": "320", "Protein": "12g", "Carbs": "28g", "Fat": "18g"}',
                  preparationTime: 15
                },
                {
                  id: 'app2',
                  name: 'Pan-Seared Scallops',
                  description: 'Fresh Atlantic scallops with cauliflower purée and pancetta crisps',
                  price: 22.99,
                  imageUrl: null,
                  allergens: 'Shellfish',
                  nutritionalInfo: '{"Calories": "280", "Protein": "24g", "Carbs": "8g", "Fat": "16g"}',
                  preparationTime: 12
                },
                {
                  id: 'app3',
                  name: 'Burrata Caprese',
                  description: 'Creamy burrata cheese with heirloom tomatoes, basil oil, and aged balsamic',
                  price: 18.99,
                  imageUrl: null,
                  allergens: 'Dairy',
                  nutritionalInfo: '{"Calories": "380", "Protein": "16g", "Carbs": "12g", "Fat": "32g"}',
                  preparationTime: 8
                }
              ]
            },
            {
              category: 'Main Courses',
              order: 2,
              items: [
                {
                  id: 'main1',
                  name: 'Dry-Aged Ribeye Steak',
                  description: '28-day aged 12oz ribeye with roasted bone marrow, truffle fries, and red wine jus',
                  price: 48.99,
                  imageUrl: null,
                  allergens: '',
                  nutritionalInfo: '{"Calories": "720", "Protein": "52g", "Carbs": "28g", "Fat": "45g"}',
                  preparationTime: 25
                },
                {
                  id: 'main2',
                  name: 'Lobster Risotto',
                  description: 'Arborio rice with fresh Maine lobster, saffron, and microgreens',
                  price: 42.99,
                  imageUrl: null,
                  allergens: 'Shellfish, Dairy',
                  nutritionalInfo: '{"Calories": "580", "Protein": "32g", "Carbs": "45g", "Fat": "28g"}',
                  preparationTime: 20
                },
                {
                  id: 'main3',
                  name: 'Wild Mushroom Gnocchi',
                  description: 'House-made potato gnocchi with wild mushrooms, brown butter, and sage',
                  price: 28.99,
                  imageUrl: null,
                  allergens: 'Gluten, Dairy, Eggs',
                  nutritionalInfo: '{"Calories": "520", "Protein": "18g", "Carbs": "52g", "Fat": "26g"}',
                  preparationTime: 18
                },
                {
                  id: 'main4',
                  name: 'Herb-Crusted Salmon',
                  description: 'Atlantic salmon with lemon herb crust, quinoa salad, and citrus vinaigrette',
                  price: 34.99,
                  imageUrl: null,
                  allergens: 'Fish',
                  nutritionalInfo: '{"Calories": "480", "Protein": "38g", "Carbs": "32g", "Fat": "22g"}',
                  preparationTime: 16
                }
              ]
            },
            {
              category: 'Desserts',
              order: 3,
              items: [
                {
                  id: 'dess1',
                  name: 'Chocolate Lava Cake',
                  description: 'Warm chocolate cake with molten center, vanilla gelato, and berry compote',
                  price: 12.99,
                  imageUrl: null,
                  allergens: 'Gluten, Dairy, Eggs',
                  nutritionalInfo: '{"Calories": "420", "Protein": "8g", "Carbs": "48g", "Fat": "22g"}',
                  preparationTime: 10
                },
                {
                  id: 'dess2',
                  name: 'Tiramisu',
                  description: 'Classic Italian dessert with espresso-soaked ladyfingers and mascarpone',
                  price: 10.99,
                  imageUrl: null,
                  allergens: 'Gluten, Dairy, Eggs',
                  nutritionalInfo: '{"Calories": "380", "Protein": "7g", "Carbs": "32g", "Fat": "24g"}',
                  preparationTime: 5
                },
                {
                  id: 'dess3',
                  name: 'Seasonal Fruit Tart',
                  description: 'Pastry shell with vanilla custard and fresh seasonal fruits',
                  price: 11.99,
                  imageUrl: null,
                  allergens: 'Gluten, Dairy, Eggs',
                  nutritionalInfo: '{"Calories": "340", "Protein": "6g", "Carbs": "38g", "Fat": "18g"}',
                  preparationTime: 5
                }
              ]
            },
            {
              category: 'Beverages',
              order: 4,
              items: [
                {
                  id: 'bev1',
                  name: 'Signature Cocktail - Bella Vista',
                  description: 'Hendricks gin, elderflower, cucumber, lime, and prosecco',
                  price: 16.99,
                  imageUrl: null,
                  allergens: '',
                  nutritionalInfo: '{"Calories": "180", "Alcohol": "14%"}',
                  preparationTime: 3
                },
                {
                  id: 'bev2',
                  name: 'Chianti Classico DOCG',
                  description: 'Full-bodied Tuscan red wine with notes of cherry and spice (750ml)',
                  price: 45.99,
                  imageUrl: null,
                  allergens: 'Sulfites',
                  nutritionalInfo: '{"Calories": "125 per glass", "Alcohol": "13.5%"}',
                  preparationTime: 1
                },
                {
                  id: 'bev3',
                  name: 'Artisan Coffee',
                  description: 'Single-origin Colombian beans, freshly roasted and expertly brewed',
                  price: 4.99,
                  imageUrl: null,
                  allergens: '',
                  nutritionalInfo: '{"Calories": "5", "Caffeine": "95mg"}',
                  preparationTime: 4
                }
              ]
            }
          ]
        }
      }
    }
  }
}
</script>

<style scoped>
.hero-gradient {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #7209b7 100%);
  position: relative;
}

.hero-gradient::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(120, 9, 183, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(83, 52, 131, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(15, 52, 96, 0.2) 0%, transparent 50%);
  pointer-events: none;
}

.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.card-glass {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border-radius: 20px;
}

.animate-glow {
  animation: glow 3s ease-in-out infinite alternate;
}

@keyframes glow {
  from {
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2),
      0 0 20px rgba(102, 126, 234, 0.3);
  }
  to {
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2),
      0 0 40px rgba(118, 75, 162, 0.4);
  }
}

/* Custom colors matching the theme */
.text-primary-400 {
  color: #818cf8;
}

.border-primary-400 {
  border-color: #818cf8;
}

.dark-700 {
  background-color: #374151;
}

.dark-800 {
  background-color: #1f2937;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid {
    grid-template-columns: 1fr;
  }
  
  .card-glass {
    padding: 1.5rem;
  }
  
  .text-4xl {
    font-size: 2.5rem;
  }
  
  .text-5xl {
    font-size: 3rem;
  }
}

@media (max-width: 640px) {
  .px-6 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* Time-based theme switching */

/* Dark Theme (default) */
.dark-theme {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #7209b7 100%);
}

.dark-theme .menu-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border-radius: 20px;
}

.dark-theme .menu-item-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  transition: all 0.3s ease;
}

.dark-theme .menu-item-card:hover {
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  transform: translateY(-2px);
}

.dark-theme .menu-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dark-theme .menu-info {
  color: #e2e8f0;
}

.dark-theme .menu-icon {
  color: #818cf8;
}

.dark-theme .menu-item-title {
  color: #ffffff;
}

.dark-theme .menu-item-description {
  color: #cbd5e1;
}

.dark-theme .menu-price {
  color: #fbbf24;
  text-shadow: 0 0 10px rgba(251, 191, 36, 0.3);
}

.dark-theme .menu-extra-info {
  color: #94a3b8;
}

.dark-theme .menu-allergen-info {
  color: #fca5a5;
}

.dark-theme .menu-nutritional-info {
  color: #a3a3a3;
}

.dark-theme .menu-footer {
  color: #94a3b8;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.dark-theme .theme-toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.dark-theme .preview-banner {
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
}

.dark-theme .text-yellow-200 {
  color: #fde047;
}

/* Dietary tags for dark theme */
.dark-theme .dietary-tag {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.dark-theme .dietary-tag-vegetarian {
  background: rgba(34, 197, 94, 0.2);
  color: #86efac;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.dark-theme .dietary-tag-vegan {
  background: rgba(22, 163, 74, 0.2);
  color: #74e79a;
  border: 1px solid rgba(22, 163, 74, 0.3);
}

.dark-theme .dietary-tag-gluten-free {
  background: rgba(245, 158, 11, 0.2);
  color: #fcd34d;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.dark-theme .dietary-tag-spicy {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

/* Light Theme */
.light-theme {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 25%, #f1f5f9 50%, #e2e8f0 75%, #cbd5e1 100%) !important;
}

.light-theme .menu-card,
.light-theme .card-glass {
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.light-theme .menu-item-card,
.light-theme .bg-gradient-to-br {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  color: #1f2937 !important;
}

.light-theme .menu-item-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.light-theme .menu-title,
.light-theme .text-gradient {
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
}

.light-theme .menu-info,
.light-theme .text-gray-300 {
  color: #6b7280 !important;
}

.light-theme .menu-icon,
.light-theme .text-primary-400 {
  color: #3b82f6 !important;
}

.light-theme .menu-item-title,
.light-theme .text-white {
  color: #1f2937 !important;
}

.light-theme .menu-item-description {
  color: #6b7280 !important;
}

.light-theme .menu-price {
  color: #059669 !important;
  text-shadow: none !important;
}

.light-theme .menu-extra-info {
  color: #6b7280 !important;
}

.light-theme .menu-allergen-info {
  color: #dc2626 !important;
}

.light-theme .menu-nutritional-info {
  color: #6b7280 !important;
}

.light-theme .menu-footer {
  color: #6b7280 !important;
  border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.light-theme .theme-toggle-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.light-theme .preview-banner {
  background: rgba(245, 158, 11, 0.1) !important;
  border: 1px solid rgba(245, 158, 11, 0.3) !important;
}

.light-theme .text-yellow-200 {
  color: #d97706 !important;
}

/* Light theme dietary tags */
.light-theme .dietary-tag-vegetarian {
  background: rgba(34, 197, 94, 0.1) !important;
  color: #059669 !important;
  border: 1px solid rgba(34, 197, 94, 0.2) !important;
}

.light-theme .dietary-tag-vegan {
  background: rgba(22, 163, 74, 0.1) !important;
  color: #047857 !important;
  border: 1px solid rgba(22, 163, 74, 0.2) !important;
}

.light-theme .dietary-tag-gluten-free {
  background: rgba(245, 158, 11, 0.1) !important;
  color: #d97706 !important;
  border: 1px solid rgba(245, 158, 11, 0.2) !important;
}

.light-theme .dietary-tag-spicy {
  background: rgba(239, 68, 68, 0.1) !important;
  color: #dc2626 !important;
  border: 1px solid rgba(239, 68, 68, 0.2) !important;
}
</style>
